import { useCallback, useEffect, useState } from 'react';
import type { CreateModelPackageInput, HardwareAsset, Model, ModelPackage } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';

/**
 * Packaging de modèle (ADR-0034). Charge les paquets de l'organisation, ainsi que les **modèles**
 * (pour choisir un modèle validé) et la **flotte** (pour choisir une cible + lire son enveloppe
 * calcul). Chaque écriture rafraîchit la liste des paquets — le registre reste modeste. Réutilise
 * `apiFetch` ; les erreurs (dont le 503 dormant du lancement) sont remontées à l'appelant.
 */
export function usePackages() {
  const [packages, setPackages] = useState<ModelPackage[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [assets, setAssets] = useState<HardwareAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      setPackages(await apiFetch<ModelPackage[]>('/packages'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const [p, m, a] = await Promise.all([
          apiFetch<ModelPackage[]>('/packages'),
          apiFetch<Model[]>('/models').catch(() => [] as Model[]),
          apiFetch<HardwareAsset[]>('/hardware').catch(() => [] as HardwareAsset[]),
        ]);
        if (!active) return;
        setPackages(p);
        setModels(m);
        setAssets(a);
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'Chargement impossible');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const create = useCallback(async (input: CreateModelPackageInput) => {
    const created = await apiFetch<ModelPackage>('/packages', { method: 'POST', body: input });
    setPackages((prev) => [created, ...prev]);
  }, []);

  const launch = useCallback(async (id: string) => {
    const updated = await apiFetch<ModelPackage>(`/packages/${id}/launch`, { method: 'POST' });
    setPackages((prev) => prev.map((p) => (p.id === id ? updated : p)));
  }, []);

  const remove = useCallback(async (id: string) => {
    await apiFetch<void>(`/packages/${id}`, { method: 'DELETE' });
    setPackages((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return { packages, models, assets, loading, error, refresh, create, launch, remove };
}
